/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import TourPanel from "./TourPanel";
import "./InfoTip.css";

// The card's own width, from TourPanel.css. Known here because the placement
// arithmetic below needs it before the card exists.
const CARD = 380;

// Gap between the button and the card, and the margin kept against the edge
// of the viewport. Both in px.
const GAP = 8;
const EDGE = 16;

// Below this much room underneath the button, the card opens upward instead.
const ROOM = 260;

/**
 * An always-on (i) button (CP-308).
 *
 * The deck marks four of its fourteen steps with an always-on column, and
 * this is that column: a button small enough to ignore that reopens *that
 * step's own panel*. Not a smaller version of it, not a paraphrase — the
 * same card the tour draws, through the same TourPanel component, with the
 * same section label, heading and wording. Two differences the deck asks
 * for: a single Close on the button row, where a running tour would offer
 * Back, Next and Skip; and no step badge, because this is an examples box
 * rather than a position in a run nobody is in.
 *
 * Reopening the step rather than restating it is the point. The guidance the
 * tour gives for a goal or a metric is worth as much on the day an
 * organisation adds its third one, months after the tour has been and gone.
 * The copy has exactly one home, MARKETPLACE.tour.steps, and the card has
 * exactly one implementation.
 *
 * The panel is rendered into document.body through a portal rather than
 * beside the button, and positioned from the button's own box. It has to be:
 * the profile card carries `overflow: hidden` so that the cover image is
 * clipped to its rounded corners, and anything absolutely positioned inside
 * it is clipped to the card too — the panel came out cut off at the card's
 * bottom edge with the next section drawn over it. No z-index fixes that,
 * because clipping is not stacking. Leaving the DOM subtree altogether
 * escapes both, and is the same thing GuidedTour does for the same reason.
 *
 * Local state rather than the view model, deliberately. Whether a panel is
 * open is nobody's business but this button's — it survives nothing, affects
 * nothing else on the page, and threading four more booleans through
 * Marketplace's state would be four more things to reset on logout.
 */
class InfoTip extends Component {
  constructor(props) {
    super(props);
    this.state = { open: false, at: null };
    // The button, which the panel is positioned against; and the panel, which
    // an outside-click test has to know about even though the portal puts it
    // outside this component's own subtree.
    this.button = React.createRef();
    this.panel = React.createRef();
  }

  componentDidMount() {
    // Capture, so the panel closes before whatever was pressed acts on it —
    // a press on "Edit cover image" should not leave a panel hanging over
    // the modal it opens.
    document.addEventListener("mousedown", this.onOutside, true);
    document.addEventListener("keydown", this.onKey);
    window.addEventListener("resize", this.place);
    // Capture again: the box that moves is the content column's, not the
    // window's, and a listener on the window alone never hears it.
    window.addEventListener("scroll", this.place, true);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.onOutside, true);
    document.removeEventListener("keydown", this.onKey);
    window.removeEventListener("resize", this.place);
    window.removeEventListener("scroll", this.place, true);
  }

  onOutside = (e) => {
    if (!this.state.open) {
      return;
    }
    const button = this.button.current;
    const panel = this.panel.current;
    // The panel is not inside this component's DOM any more, so both have to
    // be asked. Without the second test, a press on the panel's own Close
    // would read as a press outside it.
    if (
      (button && button.contains(e.target)) ||
      (panel && panel.contains(e.target))
    ) {
      return;
    }
    this.setState({ open: false });
  };

  onKey = (e) => {
    if (this.state.open && e.key === "Escape") {
      this.setState({ open: false });
    }
  };

  /**
   * Where the panel goes, in viewport coordinates.
   *
   * Fixed rather than absolute, because the portal puts it on the body: it
   * has to follow the button as the page scrolls rather than sit at a
   * document offset. Clamped to the viewport on both axes, and flipped above
   * the button when there is not enough room below it.
   */
  place = () => {
    if (!this.state.open) {
      return;
    }
    const button = this.button.current;
    if (!button || typeof button.getBoundingClientRect !== "function") {
      return;
    }
    const box = button.getBoundingClientRect();
    const width = window.innerWidth;
    const height = window.innerHeight;
    const wide = Math.min(CARD, width - EDGE * 2);
    // "right" opens rightwards from the button, which suits one sitting at
    // the left end of a heading; "left" ends level with it.
    const raw =
      this.props.place === "left" ? box.right + GAP - wide : box.left - GAP;
    const at = {
      left: Math.min(Math.max(EDGE, raw), Math.max(EDGE, width - wide - EDGE)),
    };
    if (box.bottom + GAP + ROOM > height && box.top > height - box.bottom) {
      at.bottom = height - box.top + GAP;
    } else {
      at.top = box.bottom + GAP;
    }
    const was = this.state.at;
    if (
      !was ||
      was.left !== at.left ||
      was.top !== at.top ||
      was.bottom !== at.bottom
    ) {
      this.setState({ at: at });
    }
  };

  // Stopped from propagating: several of these sit inside a section head or a
  // form row that has a click handler of its own, and opening a panel must
  // not also open the form it is describing.
  toggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    this.setState(
      (s) => ({ open: !s.open, at: s.open ? null : s.at }),
      this.place
    );
  };

  close = (e) => {
    e.stopPropagation();
    e.preventDefault();
    this.setState({ open: false, at: null });
  };

  render() {
    const tip = this.props.tip;
    // Nothing to say, nothing to press. A step with no copy behind it renders
    // no button rather than one that opens an empty panel — which is also
    // what the supporter's view of this page gets, since it is handed none.
    if (!tip || !tip.body) {
      return null;
    }
    const open = this.state.open;
    // The tour's own shape, with the button row the deck asks for. Styled as
    // the primary because it is the only thing on the row: an outline would
    // be deferring to an action that is not there.
    const panel = {
      countLabel: tip.countLabel,
      kicker: tip.kicker,
      title: tip.title,
      body: tip.body,
      buttons: [
        { id: "close", kind: "primary", label: tip.close, press: this.close },
      ],
    };
    return (
      <span className="Marketplace-InfoTip">
        <span
          className={
            "Marketplace-InfoTip-btn" + (open ? " Marketplace-InfoTip-btn--on" : "")
          }
          ref={this.button}
          role="button"
          tabIndex={0}
          aria-expanded={open}
          aria-label={tip.open || tip.title}
          title={tip.open || tip.title}
          onClick={this.toggle}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              this.toggle(e);
            }
          }}
        >
          i
        </span>
        {open &&
          ReactDOM.createPortal(
            <div
              className="Marketplace-InfoTip-pop"
              ref={this.panel}
              // Hidden until placed, so it cannot appear at the top-left
              // corner for the frame between opening and being measured.
              style={this.state.at || { visibility: "hidden" }}
            >
              <TourPanel panel={panel} />
            </div>,
            document.body
          )}
      </span>
    );
  }
}

InfoTip.propTypes = {
  // One step's panel: { kicker, title, body, close, open }, built by
  // Marketplace's tipsVm straight off MARKETPLACE.tour.steps. No countLabel:
  // TourPanel draws no step badge without one, which is the point.
  tip: PropTypes.object,
  place: PropTypes.string,
};

export default InfoTip;
